/**
 * Definition structured data (JSON-LD) for key terms and glossary items
 * Optimized for AEO (Answer Engine Optimization) and GEO (Generative Engine Optimization)
 * Helps answer engines extract definitions directly
 * Follows Schema.org DefinedTerm specification
 */

import React from 'react';
import { JSONLDScript } from './jsonld-script';

/**
 * A single term definition
 */
export interface TermDefinition {
  /** Term name */
  name: string;
  /** Term definition/description */
  description: string;
  /** Optional URL to term definition page */
  url?: string;
  /** Optional related terms */
  relatedTerms?: string[];
}

interface DefinitionStructuredDataProps {
  /** Term definitions */
  terms: TermDefinition[];
  /** Optional term set name (e.g., "Email Ferret Glossary") */
  termSetName?: string;
  /** Optional term set URL */
  termSetUrl?: string;
}

/**
 * Definition structured data component
 *
 * Generates Schema.org DefinedTerm JSON-LD for term definitions.
 * This helps AI-powered search engines extract definitions directly
 * when users ask "What is X?" questions.
 *
 * @example
 * ```tsx
 * <DefinitionStructuredData
 *   terms={[
 *     {
 *       name: "Graymail",
 *       description: "Legitimate emails that you don't want, such as newsletters and marketing emails that you subscribed to but no longer read.",
 *       relatedTerms: ["Cold Outreach", "Spam"]
 *     },
 *     {
 *       name: "Cold Outreach",
 *       description: "Unsolicited emails from people or companies you don't have a relationship with, typically for sales or networking purposes."
 *     }
 *   ]}
 *   termSetName="Email Ferret Glossary"
 * />
 * ```
 *
 * @see https://schema.org/DefinedTerm for complete schema specification
 */
export function DefinitionStructuredData({
  terms,
  termSetName = 'Email Ferret Glossary',
  termSetUrl,
}: DefinitionStructuredDataProps) {
  const baseUrl =
    process.env.NEXT_PUBLIC_APP_URL || process.env.APP_URL || 'https://emailferret.io';
  const setUrl = termSetUrl || `${baseUrl}/glossary`;

  // If multiple terms, use DefinedTermSet; if single term, use DefinedTerm
  if (terms.length === 1) {
    const term = terms[0];
    const termSchema = {
      '@context': 'https://schema.org',
      '@type': 'DefinedTerm',
      name: term.name,
      description: term.description,
      ...(term.url && { url: term.url }),
      ...(termSetName && {
        inDefinedTermSet: {
          '@type': 'DefinedTermSet',
          name: termSetName,
          url: setUrl,
        },
      }),
      ...(term.relatedTerms &&
        term.relatedTerms.length > 0 && {
          relatedTerms: term.relatedTerms,
        }),
    };

    return <JSONLDScript data={termSchema} id="definition-structured-data" />;
  }

  // Multiple terms - use DefinedTermSet
  const termSetSchema = {
    '@context': 'https://schema.org',
    '@type': 'DefinedTermSet',
    name: termSetName,
    url: setUrl,
    hasDefinedTerm: terms.map((term) => ({
      '@type': 'DefinedTerm',
      name: term.name,
      description: term.description,
      ...(term.url && { url: term.url }),
      ...(term.relatedTerms &&
        term.relatedTerms.length > 0 && {
          relatedTerms: term.relatedTerms,
        }),
    })),
  };

  return <JSONLDScript data={termSetSchema} id="definition-structured-data" />;
}
